/**
 * The right-hand drawer the dev tools overlay lives in.
 *
 * Owns the element, its visibility and its width — everything except what goes inside it. eruda is
 * handed the nested `mount` element rather than the drawer itself, because it stamps `id="eruda"` and
 * `style.all = "initial"` onto whatever container it is given, which would flatten the positioning here.
 *
 * The width is drag-adjustable from the left edge and persisted, so it survives reloads and the OIDC
 * round-trip of a fresh desktop launch.
 */

const DRAWER_ID = 'comptaai-devtools';
const WIDTH_KEY = 'devtools-width';

const HANDLE_WIDTH_PX = 6;
const MIN_WIDTH_PX = 320;

/** Always leave a sliver of app visible, so a drawer dragged to full width can still be grabbed back. */
const MIN_VISIBLE_APP_PX = 80;

/** Share of the viewport used the first time, before the user has dragged anything. */
const DEFAULT_WIDTH_RATIO = 0.46;
const DEFAULT_MAX_WIDTH_PX = 720;

export interface DevToolsDrawer {
    /** The element eruda renders into. Sized by the caller once eruda has finished initialising. */
    readonly mount: HTMLElement;
    show(): void;
    hide(): void;
    destroy(): void;
}

function clampWidth(px: number): number {
    const max = Math.max(MIN_WIDTH_PX, window.innerWidth - MIN_VISIBLE_APP_PX);
    return Math.min(Math.max(px, MIN_WIDTH_PX), max);
}

function defaultWidth(): number {
    return clampWidth(Math.min(window.innerWidth * DEFAULT_WIDTH_RATIO, DEFAULT_MAX_WIDTH_PX));
}

/** Reads the persisted width, clamped to the current viewport. Null when absent or unusable. */
function readStoredWidth(): number | null {
    try {
        const raw = localStorage.getItem(WIDTH_KEY);
        if (raw === null) return null;
        const value = Number(raw);
        return Number.isFinite(value) ? clampWidth(value) : null;
    } catch {
        return null;
    }
}

function storeWidth(px: number): void {
    try {
        localStorage.setItem(WIDTH_KEY, String(Math.round(px)));
    } catch {
        // Private mode / quota — the drawer just won't remember its width across reloads.
    }
}

function buildDrawerStyle(): string {
    return [
        'position:fixed',
        'top:0',
        'right:0',
        'bottom:0',
        'display:none',
        'box-sizing:border-box',
        `padding-left:${HANDLE_WIDTH_PX}px`,
        'box-shadow:-2px 0 12px rgba(0,0,0,0.35)',
        // Above every app layer — modals and toasts must not cover the inspector.
        'z-index:2147483646',
    ].join(';');
}

function buildHandleStyle(): string {
    return [
        'position:absolute',
        'top:0',
        'left:0',
        'bottom:0',
        `width:${HANDLE_WIDTH_PX}px`,
        'cursor:col-resize',
        // Neutral grey reads against both the light and dark eruda themes.
        'background:rgba(128,128,128,0.35)',
        'touch-action:none',
    ].join(';');
}

/** Builds the drawer, wires its resize handle, and appends it to the document. Starts hidden. */
export function createDevToolsDrawer(): DevToolsDrawer {
    const host = document.createElement('div');
    host.id = DRAWER_ID;
    host.style.cssText = buildDrawerStyle();

    const handle = document.createElement('div');
    handle.style.cssText = buildHandleStyle();

    const mount = document.createElement('div');
    host.append(handle, mount);
    document.body.appendChild(host);

    // Tracked here rather than read back from layout: the drawer can be resized while hidden, when
    // getBoundingClientRect would report nothing useful.
    let width = readStoredWidth() ?? defaultWidth();
    const applyWidth = (next: number): void => {
        width = clampWidth(next);
        host.style.width = `${width}px`;
    };
    applyWidth(width);

    let dragStartX = 0;
    let dragStartWidth = 0;
    let dragging = false;

    const handlePointerMove = (event: PointerEvent): void => {
        // Anchored to the right edge, so dragging left widens the drawer.
        applyWidth(dragStartWidth + (dragStartX - event.clientX));
    };

    const handlePointerUp = (): void => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        document.body.style.userSelect = '';
        // Only a real drag end persists. destroy() reuses this to detach, and a viewport shrink re-clamps
        // without going through it — so neither overwrites the width the user actually chose.
        if (dragging) storeWidth(width);
        dragging = false;
    };

    const handlePointerDown = (event: PointerEvent): void => {
        // Suppress the text selection that a drag across the app would otherwise start.
        event.preventDefault();
        dragStartX = event.clientX;
        dragStartWidth = width;
        dragging = true;
        document.body.style.userSelect = 'none';
        // Listening on window, not the handle: the pointer leaves the 6px strip on the first move, and
        // window still sees events retargeted out of eruda's shadow root.
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    // A viewport that shrinks below the current width would otherwise push the app off-screen entirely.
    const handleViewportResize = (): void => applyWidth(width);

    handle.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleViewportResize);

    return {
        mount,
        show: () => (host.style.display = 'block'),
        hide: () => (host.style.display = 'none'),
        destroy: () => {
            handlePointerUp();
            handle.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('resize', handleViewportResize);
            host.remove();
        },
    };
}
